// Configuration des types d'activité pour les rapports d'activité (cards)

import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Popconfirm,
  Typography,
  Modal,
  Form,
  Input,
  Divider,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { useAppTranslation } from '../../../hooks/useAppTranslation';

const { Text } = Typography;

interface ActivityTypesStepProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  showDivider?: boolean;
  description?: string;
}

const ActivityTypesStep: React.FC<ActivityTypesStepProps> = ({
  value = [],
  onChange,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
  showDivider = true,
  description,
}) => {
  const { t } = useAppTranslation(['activity', 'common']);
  const resolvedDescription = description ?? t('activityTypes.stepDescription');

  const [activityTypes, setActivityTypes] = useState<string[]>(value);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form] = Form.useForm<{ label: string }>();

  useEffect(() => {
    setActivityTypes(value ?? []);
  }, [value]);

  const handleChange = (newList: string[]) => {
    setActivityTypes(newList);
    onChange?.(newList);
  };

  const handleAdd = () => {
    setEditingIndex(null);
    form.resetFields();
    form.setFieldsValue({ label: '' });
    setModalOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    form.setFieldsValue({ label: activityTypes[index] });
    setModalOpen(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const label = (values.label as string)?.trim();
      if (!label) return;

      let newList: string[];
      if (editingIndex !== null) {
        newList = activityTypes.map((s, i) => (i === editingIndex ? label : s));
      } else {
        newList = [...activityTypes, label];
      }
      handleChange(newList);
      setModalOpen(false);
      setEditingIndex(null);
      form.resetFields();
    });
  };

  const handleDelete = (index: number) => {
    const newList = activityTypes.filter((_, i) => i !== index);
    handleChange(newList);
  };

  const showActions = canUpdate || canDelete;

  return (
    <>
      {showDivider && <Divider />}
      <Card
        size="small"
        title={
          <Space>
            <FileTextOutlined />
            <span>{t('activityTypes.title')}</span>
          </Space>
        }
        extra={
          canCreate ? (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAdd}>
              {t('common:actions.add')}
            </Button>
          ) : null
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {resolvedDescription}
        </Text>
        {activityTypes.length === 0 ? (
          <Text type="secondary">{t('activityTypes.empty')}</Text>
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {activityTypes.map((label, index) => (
              <Card key={`${label}-${index}`} size="small" styles={{ body: { padding: '10px 12px' } }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Text strong>{label}</Text>
                  {showActions && (
                    <Space>
                      {canUpdate && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(index)}
                        />
                      )}
                      {canDelete && (
                        <Popconfirm
                          title={t('activityTypes.deleteConfirm')}
                          onConfirm={() => handleDelete(index)}
                          okText={t('common:actions.delete')}
                          cancelText={t('common:actions.cancel')}
                        >
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </Card>
            ))}
          </Space>
        )}
      </Card>

      <Modal
        title={
          editingIndex !== null ? t('activityTypes.modalEdit') : t('activityTypes.modalAdd')
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => {
          setModalOpen(false);
          setEditingIndex(null);
          form.resetFields();
        }}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="label"
            label={t('activityTypes.fieldLabel')}
            rules={[
              { required: true, message: t('activityTypes.fieldRequired') },
              { max: 80, message: t('activityTypes.fieldMax') },
            ]}
          >
            <Input placeholder={t('activityTypes.inputPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ActivityTypesStep;
